import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useWindowSize } from "@reactuses/core";
import { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import { AnalysisEventRaw, AnalysisRawRow, getAnalysisEventRaw } from "../../api/analysis";
import { readShopZoneError } from "../../api/shopZone";
import { ModalLayout } from "../../components/common/ModalLayout";
import theme from "../../theme";
import { EVENT_TYPES, n } from "./analysisUtil";

/**
 * 이벤트 한 건의 원시 위치로그 (tb_pstn_log) — 그래프·표·엑셀.
 * 지도 팝업과 처리 결과 목록의 '원시 데이터' 버튼에서 연다.
 */

const { md } = theme.screen;
const FONT = "Noto Sans, Pretendard, sans-serif";
const WINDOWS = [1, 5, 10, 30];
/** 표는 이만큼만 그린다. 엑셀에는 전부 들어간다 */
const TABLE_MAX_ROWS = 1500;

const STTS_LABEL: Record<number, string> = { 0: "위치", 1: "시동ON", 2: "시동OFF" };
const EVT_LABEL: Record<number, string> = { 0: "정상", 1: "시동ON", 2: "시동OFF", 3: "헬스체크", 11: "전복사고" };
const STATUS_LABEL: Record<number, string> = { 0: "미처리", 1: "취소", 2: "해제" };

const codeText = (labels: Record<number, string>, v: number | null) =>
  v === null || v === undefined ? "" : labels[v] ? `${labels[v]}(${v})` : String(v);

/** 표·엑셀 열. 순서가 곧 표시 순서다 */
const RAW_COLS: { key: keyof AnalysisRawRow; label: string; text?: (r: AnalysisRawRow) => string }[] = [
  { key: "t", label: "수집 일시" },
  { key: "reg", label: "등록 일시" },
  { key: "stts", label: "단말 상태", text: (r) => codeText(STTS_LABEL, r.stts) },
  { key: "evt", label: "이벤트", text: (r) => codeText(EVT_LABEL, r.evt) },
  { key: "lat", label: "위도" },
  { key: "lot", label: "경도" },
  { key: "speed", label: "속도(km/h)" },
  { key: "heading", label: "진행방향" },
  { key: "fix", label: "GPS Fix" },
  { key: "sat", label: "위성 수" },
  { key: "hdop", label: "HDOP" },
  { key: "tiltx", label: "기울기 X(°)" },
  { key: "tilty", label: "기울기 Y(°)" },
  { key: "tiltz", label: "기울기 Z(°)" },
  { key: "gyrox", label: "자이로 X" },
  { key: "gyroy", label: "자이로 Y" },
  { key: "gyroz", label: "자이로 Z" },
  { key: "vib", label: "진동" },
  { key: "vvolt", label: "차량 전압(V)" },
  { key: "bvolt", label: "배터리 전압(V)" },
  { key: "cpu", label: "CPU 온도(℃)" },
  { key: "mvmn", label: "이동길이" },
  { key: "acml", label: "누적이동길이" },
  { key: "rsrp", label: "RSRP" },
];

/** 'yyyy-MM-dd HH:mm:ss' (현지 시각) → epoch ms */
const toMs = (t: string) => new Date(t.replace(" ", "T")).getTime();

const cellText = (r: AnalysisRawRow, c: (typeof RAW_COLS)[number]) => {
  if (c.text) return c.text(r);
  const v = r[c.key];
  return v === null || v === undefined ? "" : String(v);
};

/** 엑셀에는 숫자는 숫자로 넣는다 */
const cellValue = (r: AnalysisRawRow, c: (typeof RAW_COLS)[number]) => {
  if (c.text) return c.text(r);
  const v = r[c.key];
  if (v === null || v === undefined || v === "") return "";
  if (c.key === "t" || c.key === "reg") return String(v);
  const num = Number(v);
  return Number.isFinite(num) ? num : String(v);
};

function downloadExcel(data: AnalysisEventRaw, typeLabel: string) {
  const e = data.event;
  const info = [
    ["항목", "값"],
    ["이벤트 ID", e.id],
    ["유형", typeLabel],
    ["발생 일시", e.dt],
    ["차량", e.vhcl_nm ?? ""],
    ["차종", e.type_nm ?? ""],
    ["단말", e.dvc_sn ?? ""],
    ["소속", e.ogdp_nm ?? ""],
    ["주소", e.addr ?? ""],
    ["위도", n(e.lat)],
    ["경도", n(e.lot)],
    ["발생 속도(km/h)", n(e.speed)],
    ["처리 상태", e.status === null ? "" : STATUS_LABEL[e.status] ?? String(e.status)],
    ["해제 사유", e.rmv_rsn ?? ""],
    ["해제 일시", e.rmv_dt ?? ""],
    ["조회 구간", `${data.from ?? ""} ~ ${data.to ?? ""} (앞 ${data.beforeMin}분 / 뒤 ${data.afterMin}분)`],
    ["원시 로그 수", data.rows.length],
  ];
  const rows = [RAW_COLS.map((c) => c.label), ...data.rows.map((r) => RAW_COLS.map((c) => cellValue(r, c)))];
  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.aoa_to_sheet(rows);
  ws1["!cols"] = RAW_COLS.map((c) => ({ wch: c.key === "t" || c.key === "reg" ? 20 : Math.max(9, c.label.length + 4) }));
  const ws2 = XLSX.utils.aoa_to_sheet(info);
  ws2["!cols"] = [{ wch: 16 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, ws1, "원시데이터");
  XLSX.utils.book_append_sheet(wb, ws2, "이벤트정보");
  const stamp = e.dt.replace(/[-: ]/g, "").slice(0, 12);
  XLSX.writeFile(wb, `원시데이터_${e.dvc_sn ?? "단말"}_${stamp}_${e.id}.xlsx`);
}

/** 원시 로그 한 종류를 선 그래프로 */
function RawChart({
  title,
  rows,
  eventMs,
  lines,
  height,
}: {
  title: string;
  rows: AnalysisRawRow[];
  eventMs: number;
  lines: { key: keyof AnalysisRawRow; name: string; color: string }[];
  height: number;
}) {
  const series = useMemo(
    () =>
      lines.map((l) => ({
        name: l.name,
        data: rows
          .filter((r) => r[l.key] !== null && r[l.key] !== undefined && r[l.key] !== "")
          .map((r) => [toMs(r.t), Number(r[l.key])] as [number, number]),
      })),
    [rows, lines]
  );
  return (
    <ChartBox>
      <h3>{title}</h3>
      <Chart
        type="line"
        height={height}
        series={series}
        options={{
          chart: { fontFamily: FONT, toolbar: { show: false }, zoom: { enabled: true, type: "x" }, animations: { enabled: false } },
          colors: lines.map((l) => l.color),
          stroke: { width: 1.6, curve: "straight" },
          markers: { size: 0 },
          dataLabels: { enabled: false },
          legend: { position: "top", fontSize: "11px", height: 22 },
          xaxis: {
            type: "datetime",
            labels: { datetimeUTC: false, format: "HH:mm:ss", style: { fontSize: "10px" } },
            tooltip: { enabled: false },
          },
          yaxis: { labels: { formatter: (v: number) => (Number.isFinite(v) ? String(Math.round(v * 100) / 100) : ""), style: { fontSize: "10px" } } },
          tooltip: { shared: true, x: { format: "HH:mm:ss" } },
          annotations: {
            xaxis: [
              {
                x: eventMs,
                borderColor: "#111827",
                strokeDashArray: 4,
                label: { text: "이벤트", style: { fontSize: "10px", color: "#fff", background: "#111827" } },
              },
            ],
          },
          grid: { strokeDashArray: 3 },
        }}
      />
    </ChartBox>
  );
}

interface Props {
  eventId: number | null;
  onClose: () => void;
}

export default function EventRawModal({ eventId, onClose }: Props) {
  const { width } = useWindowSize();
  const isMobile = width <= md;
  const [win, setWin] = useState(5);
  const q = useQuery({
    queryKey: ["analysis", "raw", eventId, win],
    queryFn: () => getAnalysisEventRaw(eventId as number, win, win),
    enabled: eventId !== null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const data = q.data;
  const e = data?.event;
  const meta = e ? EVENT_TYPES[e.type] : undefined;
  const typeLabel = meta?.label ?? (e ? `유형 ${e.type}` : "");
  const eventMs = e ? toMs(e.dt) : 0;
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // 이벤트 시각에 가장 가까운 줄을 표에서 강조한다
  const nearest = useMemo(() => {
    if (!rows.length || !eventMs) return -1;
    let best = 0;
    let bestD = Infinity;
    rows.forEach((r, i) => {
      const d = Math.abs(toMs(r.t) - eventMs);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  }, [rows, eventMs]);

  const chartH = isMobile ? 200 : 220;

  return (
    <ModalLayout
      open={eventId !== null}
      onClose={onClose}
      width="1440px"
      title="이벤트 원시 데이터"
      pt="60px"
      pb="24px"
      fullScreen={isMobile}
    >
      <Body>
        {q.isLoading && <p className="note">원시 데이터를 불러오는 중…</p>}
        {q.error && <p className="note bad">불러오지 못했다 — {readShopZoneError(q.error).message}</p>}
        {e && data && (
          <>
            <Info>
              <div className="head">
                <b style={{ color: meta?.color }}>{typeLabel}</b>
                <span>{e.dt}</span>
                <span className="id">#{e.id}</span>
              </div>
              <dl>
                <div>
                  <dt>차량</dt>
                  <dd>
                    {e.vhcl_nm ?? "-"}
                    {e.type_nm ? ` (${e.type_nm})` : ""}
                  </dd>
                </div>
                <div>
                  <dt>단말</dt>
                  <dd>{e.dvc_sn ?? "-"}</dd>
                </div>
                <div>
                  <dt>소속</dt>
                  <dd>{e.ogdp_nm ?? "-"}</dd>
                </div>
                <div>
                  <dt>발생 속도</dt>
                  <dd>{e.speed ?? "-"} km/h</dd>
                </div>
                <div className="wide">
                  <dt>주소</dt>
                  <dd>
                    {e.addr || "-"}
                    {n(e.lat) > 0 && (
                      <a
                        href={`https://map.kakao.com/link/map/${encodeURIComponent(typeLabel)},${e.lat},${e.lot}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        지도
                      </a>
                    )}
                  </dd>
                </div>
                <div className="wide">
                  <dt>처리</dt>
                  <dd>
                    {e.status === null ? "-" : STATUS_LABEL[e.status] ?? e.status}
                    {e.rmv_rsn ? ` · ${e.rmv_rsn}` : ""}
                    {e.rmv_dt ? ` (${e.rmv_dt})` : ""}
                  </dd>
                </div>
              </dl>
            </Info>

            <Toolbar>
              <span className="label">이벤트 앞뒤</span>
              <div className="seg" role="group" aria-label="조회 구간">
                {WINDOWS.map((m) => (
                  <button key={m} type="button" aria-pressed={win === m} onClick={() => setWin(m)}>
                    ±{m}분
                  </button>
                ))}
              </div>
              <span className="sub">
                {data.from} ~ {data.to} · 원시 로그 {rows.length.toLocaleString()}줄
                {q.isFetching && " · 불러오는 중…"}
              </span>
              <span className="spacer" />
              <button type="button" className="excel" disabled={!rows.length} onClick={() => downloadExcel(data, typeLabel)}>
                엑셀 다운로드
              </button>
            </Toolbar>

            {!rows.length ? (
              <p className="note">이 구간에 단말이 보낸 위치로그가 없다. 구간을 넓혀 본다.</p>
            ) : (
              <>
                <Charts>
                  <RawChart
                    title="기울기 (°)"
                    rows={rows}
                    eventMs={eventMs}
                    height={chartH}
                    lines={[
                      { key: "tiltx", name: "X", color: "#d7263d" },
                      { key: "tilty", name: "Y", color: "#2563eb" },
                      { key: "tiltz", name: "Z", color: "#25863F" },
                    ]}
                  />
                  <RawChart
                    title="자이로"
                    rows={rows}
                    eventMs={eventMs}
                    height={chartH}
                    lines={[
                      { key: "gyrox", name: "X", color: "#d7263d" },
                      { key: "gyroy", name: "Y", color: "#2563eb" },
                      { key: "gyroz", name: "Z", color: "#25863F" },
                    ]}
                  />
                  <RawChart
                    title="속도 (km/h) · 진동"
                    rows={rows}
                    eventMs={eventMs}
                    height={chartH}
                    lines={[
                      { key: "speed", name: "속도", color: "#f28c28" },
                      { key: "vib", name: "진동", color: "#6b7280" },
                    ]}
                  />
                  <RawChart
                    title="전압 (V)"
                    rows={rows}
                    eventMs={eventMs}
                    height={chartH}
                    lines={[
                      { key: "vvolt", name: "차량", color: "#7c3aed" },
                      { key: "bvolt", name: "배터리", color: "#0ea5e9" },
                    ]}
                  />
                </Charts>

                <TableWrap>
                  <table>
                    <thead>
                      <tr>
                        {RAW_COLS.map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, TABLE_MAX_ROWS).map((r, i) => (
                        <tr key={`${r.t}-${i}`} className={i === nearest ? "hit" : r.evt === 11 ? "evt" : undefined}>
                          {RAW_COLS.map((c) => (
                            <td key={c.key}>{cellText(r, c)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
                <p className="note">
                  진하게 표시한 줄이 이벤트 시각에 가장 가까운 로그다. 연한 빨강은 단말이 전복사고(11)로 보낸 로그다.
                  {rows.length > TABLE_MAX_ROWS &&
                    ` 표에는 앞 ${TABLE_MAX_ROWS.toLocaleString()}줄만 보인다. 엑셀에는 전부 들어 있다.`}
                </p>
              </>
            )}
          </>
        )}
      </Body>
    </ModalLayout>
  );
}

const Body = styled.div`
  width: min(1440px, calc(100vw - 112px));
  padding: 0 4px 4px;
  font-size: 13px;
  color: ${theme.color.neutral_800};
  .note {
    margin: 8px 0;
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
  .note.bad {
    color: ${theme.color.red_600};
  }
  @media (max-width: ${md}px) {
    width: auto;
    padding: 0 12px 12px;
  }
`;

const Info = styled.div`
  border: 1px solid ${theme.color.neutral_100};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #fafafa;
  .head {
    display: flex;
    gap: 10px;
    align-items: baseline;
    margin-bottom: 8px;
    font-size: 14px;
  }
  .head .id {
    color: ${theme.color.neutral_400};
    font-size: 12px;
  }
  dl {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px 16px;
    margin: 0;
  }
  dl > div {
    min-width: 0;
  }
  dl > div.wide {
    grid-column: span 2;
  }
  dt {
    font-size: 11.5px;
    color: ${theme.color.neutral_500};
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  dd a {
    margin-left: 6px;
    font-size: 12px;
    color: ${theme.color.green_500};
  }
  @media (max-width: ${md}px) {
    dl {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
  .label {
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
  .sub {
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
  .spacer {
    flex: 1 1 auto;
  }
  .seg {
    display: inline-flex;
  }
  .seg button {
    font-size: 12px;
    padding: 4px 10px;
    border: 1px solid ${theme.color.neutral_300};
    background: #fff;
    color: ${theme.color.neutral_700};
    cursor: pointer;
    margin-left: -1px;
  }
  .seg button:first-of-type {
    border-radius: 4px 0 0 4px;
    margin-left: 0;
  }
  .seg button:last-of-type {
    border-radius: 0 4px 4px 0;
  }
  .seg button[aria-pressed="true"] {
    background: ${theme.color.green_500};
    border-color: ${theme.color.green_500};
    color: #fff;
    position: relative;
  }
  .excel {
    font-size: 12px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid ${theme.color.green_500};
    background: ${theme.color.green_500};
    color: #fff;
    cursor: pointer;
  }
  .excel:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const Charts = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px 16px;
  margin-bottom: 8px;
  @media (max-width: ${md}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ChartBox = styled.div`
  min-width: 0;
  border: 1px solid ${theme.color.neutral_100};
  border-radius: 8px;
  padding: 6px 8px 0;
  h3 {
    margin: 2px 4px 0;
    font-size: 12.5px;
    font-weight: 700;
  }
`;

const TableWrap = styled.div`
  max-height: 340px;
  overflow: auto;
  border: 1px solid ${theme.color.neutral_100};
  border-radius: 8px;
  table {
    border-collapse: collapse;
    font-size: 12px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f3f4f6;
    font-weight: 600;
    text-align: center;
    padding: 6px 8px;
    border-bottom: 1px solid ${theme.color.neutral_200};
  }
  td {
    padding: 4px 8px;
    border-bottom: 1px solid #f0f1f3;
    text-align: right;
  }
  td:nth-of-type(-n + 4) {
    text-align: left;
  }
  tr.evt td {
    background: #fff1f2;
  }
  tr.hit td {
    background: #fde68a;
    font-weight: 700;
  }
`;
